class AgeVerification extends HTMLElement {

  static HTML_BLOCKING_CLASS   = 'age-verification-active';
  static OVERLAY_VISIBLE_CLASS = 'age-verification-overlay--visible';
  static ENTRANCE_DELAY_MS     = 60;
  static FALLBACK_COOKIE_DAYS  = 30;
  static FALLBACK_REDIRECT     = 'https://www.google.com';

  static FOCUSABLE_SELECTOR = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])',
  ].join(', ');


  constructor() {
    super();

    this.previouslyFocusedElement = null;
    this.isGateActive             = false;
    this.handleOverlayClick  = this.handleOverlayClick.bind(this);
    this.handleGlobalKeydown = this.handleGlobalKeydown.bind(this);
    this.handleAnchorClick   = this.handleAnchorClick.bind(this);
    this.handlePopState      = this.handlePopState.bind(this);
    this.handleContextMenu   = this.handleContextMenu.bind(this);
  }

  connectedCallback() {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.init());
    } else {
      this.init();
    }
  }

  getConfig() {
    return {
      cookieName     : this.dataset.ageGateCookieName || 'age_verified',
      cookieDays     : parseInt(this.dataset.ageGateCookieDays, 10)  || AgeVerification.FALLBACK_COOKIE_DAYS,
      minimumAge     : parseInt(this.dataset.ageGateMinimumAge, 10)  || 18,
      declineRedirect: (this.dataset.ageGateDeclineRedirect || '').trim() || AgeVerification.FALLBACK_REDIRECT,
    };
  }

  async init() {
    const config = this.getConfig();

    /* Step 1: Already verified — do nothing */
    if (this.cookieGet(config.cookieName) === 'true') return;

    /* Step 2: Country restriction hook (future-ready) */
    try {
      const countryCheck = await this.checkCountryRestriction();
      if (countryCheck.shouldBlock) {
        console.info('[AgeVerification] Country restriction triggered:', countryCheck.countryCode);
      }
    } catch (_) {
      /* Geo check failed — proceed normally */
    }

    /* Step 3: Block storefront + show overlay */
    this.blockStorefront();
    this.showOverlay();

    /* Step 4: Bind all events */
    this.bindEvents();
  }

  bindEvents() {
    this.addEventListener('click', this.handleOverlayClick);
    document.addEventListener('keydown', this.handleGlobalKeydown, true);
    document.addEventListener('click', this.handleAnchorClick, true);
    window.addEventListener('popstate', this.handlePopState);
    document.addEventListener('contextmenu', this.handleContextMenu, true);
  }


  /* ──────────────────────────────────────────────────────────
   * STOREFRONT ACCESS CONTROL
   * ────────────────────────────────────────────────────────── */

  blockStorefront() {
    document.documentElement.classList.add(AgeVerification.HTML_BLOCKING_CLASS);
    document.body.style.overflow = 'hidden';
    this.removeAttribute('hidden');
    this.isGateActive = true;
  }

  releaseStorefront() {
    document.documentElement.classList.remove(AgeVerification.HTML_BLOCKING_CLASS);
    document.body.style.overflow = '';
    this.isGateActive = false;

    this.classList.remove(AgeVerification.OVERLAY_VISIBLE_CLASS);

    this.addEventListener(
      'transitionend',
      () => this.setAttribute('hidden', ''),
      { once: true }
    );

    this.previouslyFocusedElement?.focus();
  }


  /* ──────────────────────────────────────────────────────────
   * OVERLAY DISPLAY
   * ────────────────────────────────────────────────────────── */

  showOverlay() {
    this.previouslyFocusedElement = document.activeElement;

    window.requestAnimationFrame(() => {
      setTimeout(() => {
        this.classList.add(AgeVerification.OVERLAY_VISIBLE_CLASS);
        this.moveFocusIntoDialog();
      }, AgeVerification.ENTRANCE_DELAY_MS);
    });
  }

  moveFocusIntoDialog() {
    this.querySelector('#age-verification-confirm-button')?.focus();
  }


  /* ──────────────────────────────────────────────────────────
   * USER ACTIONS
   * ────────────────────────────────────────────────────────── */

  handleAgeConfirmed() {
    const config         = this.getConfig();
    const rememberCheckbox = this.querySelector('#age-verification-remember-checkbox');
    const cookieDays     = (rememberCheckbox && !rememberCheckbox.checked) ? 0 : config.cookieDays;

    this.cookieSet(config.cookieName, 'true', cookieDays || config.cookieDays);
    this.announceToScreenReader('Age verified. Welcome to the site.');
    this.releaseStorefront();
  }

  handleAgeDeclined() {
    const { declineRedirect } = this.getConfig();

    this.announceToScreenReader('Access denied. You are being redirected.');
    try {
      window.location.replace(declineRedirect);
    } catch (_) {
      window.location.href = declineRedirect;
    }
  }


  /* ──────────────────────────────────────────────────────────
   * EVENT HANDLERS
   * ────────────────────────────────────────────────────────── */

  handleOverlayClick(event) {
    const actionTarget = event.target.closest('[data-age-gate-action]');
    if (!actionTarget) return;

    switch (actionTarget.dataset.ageGateAction) {
      case 'confirm': this.handleAgeConfirmed(); break;
      case 'decline': this.handleAgeDeclined();  break;
    }
  }

  handleGlobalKeydown(event) {
    if (!this.isGateActive) return;

    switch (event.key) {
      case 'Escape':
        event.preventDefault();
        event.stopPropagation();
        break;

      case 'Tab':
        this.handleFocusTrap(event);
        break;
    }
  }

  handleAnchorClick(event) {
    if (!this.isGateActive) return;

    const anchor = event.target.closest('a[href]');
    if (!anchor) return;

    if (this.querySelector('#age-verification-dialog-panel')?.contains(anchor)) return;

    event.preventDefault();
    event.stopPropagation();
  }

  handlePopState() {
    if (this.isGateActive) {
      history.pushState(null, '', window.location.href);
    }
  }

  handleContextMenu(event) {
    if (!this.isGateActive) return;
    if (this.querySelector('#age-verification-dialog-panel')?.contains(event.target)) return;
    event.preventDefault();
  }


  /* ──────────────────────────────────────────────────────────
   * FOCUS TRAP
   * ────────────────────────────────────────────────────────── */

  handleFocusTrap(event) {
    const dialog = this.querySelector('#age-verification-dialog-panel');
    if (!dialog) return;

    const focusable = Array.from(
      dialog.querySelectorAll(AgeVerification.FOCUSABLE_SELECTOR)
    ).filter((el) => !el.hasAttribute('hidden') && el.offsetParent !== null);

    if (!focusable.length) return;

    const first = focusable[0];
    const last  = focusable[focusable.length - 1];

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }


  /* ──────────────────────────────────────────────────────────
   * SCREEN READER
   * ────────────────────────────────────────────────────────── */

  announceToScreenReader(message) {
    const liveRegion = document.getElementById('age-verification-live-region');
    if (!liveRegion) return;

    liveRegion.textContent = '';
    window.requestAnimationFrame(() => {
      liveRegion.textContent = message;
    });
  }


  /* ──────────────────────────────────────────────────────────
   * COOKIE UTILITIES
   * ────────────────────────────────────────────────────────── */

  cookieSet(name, value, days) {
    const expires = new Date();
    expires.setTime(expires.getTime() + days * 24 * 60 * 60 * 1000);

    const parts = [
      `${encodeURIComponent(name)}=${encodeURIComponent(value)}`,
      `expires=${expires.toUTCString()}`,
      'path=/',
      'SameSite=Lax',
    ];

    if (window.location.protocol === 'https:') parts.push('Secure');
    document.cookie = parts.join('; ');
  }

  cookieGet(name) {
    const pattern = new RegExp(`(?:^|;\\s*)${encodeURIComponent(name)}=([^;]*)`);
    const match   = document.cookie.match(pattern);
    return match ? decodeURIComponent(match[1]) : null;
  }

  cookieDelete(name) {
    document.cookie = `${encodeURIComponent(name)}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; SameSite=Lax`;
  }

  async checkCountryRestriction() {
    return { shouldBlock: false, countryCode: null };
  }

}

customElements.define('age-verification', AgeVerification);


window.__ageVerification = {
  /** Reset the cookie and reload to re-trigger the gate. Usage: __ageVerification.reset() */
  reset() {
    const el = document.querySelector('age-verification');
    if (!el) return console.warn('[AgeVerification] Element not found.');
    el.cookieDelete(el.getConfig().cookieName);
    console.info('[AgeVerification] Cookie cleared. Reload to re-trigger the gate.');
  },

  /** Returns true if the gate is currently blocking. */
  isActive() {
    return document.querySelector('age-verification')?.isGateActive ?? false;
  },
};